// useHorizontalSeek.js
import { useState, useEffect, useRef, useCallback } from 'react';

import { PLAY_STATE } from '../resource/playerUtils.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { formatTimeStringBySec } from '../resource/formatTimeString.js';

// Configurable parameters
const HORIZONTAL_THRESHOLD = 20; // Minimum horizontal drag distance
const VERTICAL_THRESHOLD = 15; // Minimum vertical drag distance to block horizontal actions
const HORIZONTAL_RATIO = 1.5; // Horizontal/vertical ratio
const LONG_PRESS_DELAY = 300; // Long press trigger time (ms)
const SEEK_DEBOUNCE_DELAY = 4; // Reduced delay time from 16ms to 4ms for more responsive seeking
const PROGRESS_BAR_HIDE_DELAY = 500; // Progress bar hide delay (ms)
const PLAY_RESUME_DELAY = 100; // Play resume delay (ms)
const MAX_DOUBLE_CLICK_INTERVAL_MILLISECONDS = 500; // Double click interval time
const FAST_FORWARD_SECONDS = 5; // Fast forward/rewind seconds

/**
 * React hook that handles horizontal swipe seeking functionality for video players.
 *
 * Provides touch-based seeking, mouse and touch long press for 2x speed playback,
 * double-click fast forward/rewind, and progress bar display.
 * Useful for implementing TikTok-style video seeking interactions on both mobile and desktop.
 *
 * @param {Object} config - Configuration object
 * @param {HTMLElement} config.element - The DOM element to attach touch and mouse events to
 * @param {Object} config.player - Player instance with methods like currentTime(), duration(), pause(), play(), playbackRate()
 * @param {boolean} config.isActive - Whether the player is currently active
 * @param {string} config.playState - Current play state (PLAY_STATE.Playing, PLAY_STATE.Pause, etc.)
 * @param {number} config.duration - Video duration in seconds
 * @param {function} config.play - Play method from parent component
 * @param {function} config.pause - Pause method from parent component
 * @param {function} [config.onSeekStart] - Callback when seeking starts
 * @param {function} [config.onSeekEnd] - Callback when seeking ends
 * @param {function} [config.onSpeedUpStart] - Callback when speed up starts (touch or mouse long press)
 * @param {function} [config.onSpeedUpEnd] - Callback when speed up ends (touch or mouse long press)
 * @param {function} [config.onProgressUpdate] - Callback to handle progress updates from player
 * @param {function} [config.onDoubleClickFastForward] - Callback when double click fast forward (receives seconds and isRewind)
 * @param {function} [config.onSingleClick] - Callback when single click (for play/pause toggle)
 * @returns {Object} Object containing seeking state and utilities
 *
 */
const useHorizontalSeek = ({
  element = null,
  player = null,
  isActive = false,
  playState = PLAY_STATE.Pause,
  duration = 0,
  play = NULL_FUNCTION,
  pause = NULL_FUNCTION,
  onSeekStart = NULL_FUNCTION,
  onSeekEnd = NULL_FUNCTION,
  onSpeedUpStart = NULL_FUNCTION,
  onSpeedUpEnd = NULL_FUNCTION,
  onProgressUpdate = NULL_FUNCTION,
  onDoubleClickFastForward = NULL_FUNCTION,
  onSingleClick = NULL_FUNCTION,
} = {}) => {
  // Touch gesture state
  const touchStartRef = useRef({ x: 0, y: 0 });
  const [isSeeking, setIsSeeking] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [shouldShowSeekBarOverlay, setShouldShowSeekBarOverlay] =
    useState(false);
  const [isSpeedUp, setIsSpeedUp] = useState(false);
  const [shouldHideUIDelayed, setShouldHideUIDelayed] = useState(false);

  // Mouse long press state (added for desktop support)
  const mouseDownStartTime = useRef(0);
  const mouseLongPressTimeout = useRef(null);
  const hasMouseMoved = useRef(false);
  const isMouseDown = useRef(false);

  // Refs for timeouts and debouncing
  const seekTimeout = useRef(null);
  const hideUITimeout = useRef(null);
  const lastSeekTimeRef = useRef(0);
  const seekDebounceRef = useRef(null);
  const seekingStartTimeRef = useRef(0);
  const longPressTimeoutRef = useRef(null);
  const wasPlayingBeforeSeekingRef = useRef(false);
  const elementWidthRef = useRef(0); // Cache element width to avoid repeated getBoundingClientRect calls

  // Double click detection refs
  const lastClickTimeRef = useRef(0);
  const clickCountRef = useRef(0);
  const doubleClickTimeoutRef = useRef(null);

  // Format time for display
  const formatTime = useCallback(timeInSeconds => {
    if (!timeInSeconds || timeInSeconds < 0) return '0:00';
    return formatTimeStringBySec(timeInSeconds);
  }, []);

  // Get display time - use seeking time when seeking, otherwise use current time
  const displayCurrentTime = useCallback(() => {
    if (isSeeking) {
      return currentTime;
    }
    const playerCurrentTime = player?.currentTime?.();
    return playerCurrentTime !== undefined ? playerCurrentTime : currentTime;
  }, [currentTime, isSeeking, player]);

  const displayDuration = useCallback(() => {
    return player?.duration?.() || duration;
  }, [duration, player]);

  // Pre-calculate display times to avoid repeated function calls during render
  const currentDisplayTime = displayCurrentTime();
  const currentDisplayDuration = displayDuration();

  // Handle double click detection and fast forward/rewind
  const handleDoubleClick = useCallback(
    (clickX, elementRect) => {
      if (!player || !elementRect) return;

      // Calculate click position relative to element width ratio
      const position = (clickX - elementRect.left) / elementRect.width;

      // Left 1/3 rewind, right 1/3 fast forward
      if (position < 1 / 3) {
        // Rewind
        const currentPlayerTime = player?.currentTime?.() || 0;
        const newTime = Math.max(0, currentPlayerTime - FAST_FORWARD_SECONDS);
        if (typeof player.currentTime === 'function') {
          player.currentTime(newTime);
        }
        onDoubleClickFastForward(FAST_FORWARD_SECONDS, true); // isRewind = true
      } else if (position > 2 / 3) {
        // Fast forward
        const currentPlayerTime = player?.currentTime?.() || 0;
        const playerDuration = player?.duration?.() || duration;
        const newTime = Math.min(
          playerDuration,
          currentPlayerTime + FAST_FORWARD_SECONDS
        );
        if (typeof player.currentTime === 'function') {
          player.currentTime(newTime);
        }
        onDoubleClickFastForward(FAST_FORWARD_SECONDS, false); // isRewind = false
      } else {
        // do nothing
      }
    },
    [player, duration, onDoubleClickFastForward]
  );

  // Handle keyboard fast forward/rewind
  const handleKeyboardSeek = useCallback(
    (isRewind = false) => {
      if (!player || !isActive) return;

      const currentPlayerTime = player?.currentTime?.() || 0;
      const playerDuration = player?.duration?.() || duration;

      let newTime;
      if (isRewind) {
        // Rewind 5 seconds
        newTime = Math.max(0, currentPlayerTime - FAST_FORWARD_SECONDS);
      } else {
        // Fast forward 5 seconds
        newTime = Math.min(
          playerDuration,
          currentPlayerTime + FAST_FORWARD_SECONDS
        );
      }

      if (typeof player.currentTime === 'function') {
        player.currentTime(newTime);
      }

      // Trigger fast forward/rewind callback
      onDoubleClickFastForward(FAST_FORWARD_SECONDS, isRewind);

      // Provide haptic feedback (if supported)
      if (navigator.vibrate) {
        navigator.vibrate(30);
      }
    },
    [player, isActive, duration, onDoubleClickFastForward]
  );

  // Handle click event (for double click detection)
  const handleClickEvent = useCallback(
    event => {
      // If this was a long press, don't handle click
      if (isSpeedUp) return;

      const currentTime = Date.now();
      const timeSinceLastClick = currentTime - lastClickTimeRef.current;

      // Clear previous single click delayed execution
      if (doubleClickTimeoutRef.current) {
        clearTimeout(doubleClickTimeoutRef.current);
        doubleClickTimeoutRef.current = null;
      }

      if (
        timeSinceLastClick < MAX_DOUBLE_CLICK_INTERVAL_MILLISECONDS &&
        clickCountRef.current === 1
      ) {
        // This is the second click of a double click
        clickCountRef.current = 2;

        // Handle double click fast forward/rewind
        const elementRect = element?.getBoundingClientRect();
        if (elementRect && event.clientX) {
          handleDoubleClick(event.clientX, elementRect);
        }

        // Reset click count
        clickCountRef.current = 0;
        lastClickTimeRef.current = 0;
      } else {
        // This might be a single click, wait to see if there's a second click
        clickCountRef.current = 1;
        lastClickTimeRef.current = currentTime;

        // Set delayed execution, if no second click within specified time, treat as single click
        doubleClickTimeoutRef.current = setTimeout(() => {
          if (clickCountRef.current === 1) {
            // Confirm single click - only trigger when clicking the container itself
            if (event.target === element) {
              // Calculate click position relative to element width ratio
              const elementRect = element.getBoundingClientRect();
              const position =
                (event.clientX - elementRect.left) / elementRect.width;

              // Only trigger play/pause when clicking in the middle third
              if (position >= 1 / 3 && position <= 2 / 3) {
                onSingleClick(event);
              }
            }
          }
          clickCountRef.current = 0;
          lastClickTimeRef.current = 0;
          doubleClickTimeoutRef.current = null;
        }, MAX_DOUBLE_CLICK_INTERVAL_MILLISECONDS);
      }
    },
    [element, handleDoubleClick, isSpeedUp, onSingleClick]
  );

  useEffect(() => {
    if (!element || !player) return;

    let isHorizontalSwipe = false;
    let isVerticalSwipe = false;
    let hasStartedSeeking = false;
    let startTime = 0;
    let wasPlayingBeforeSeeking = false;

    const handleTouchStartNonPassive = event => {
      if (!isActive || !player) return;

      const touch = event.touches && event.touches[0];
      if (!touch) return;

      const playerCurrentTime = player?.currentTime?.() || 0;
      startTime = playerCurrentTime;
      seekingStartTimeRef.current = playerCurrentTime;

      // Record current playback state at touch start, save using ref
      const isCurrentlyPlaying = playState === PLAY_STATE.Playing;
      wasPlayingBeforeSeeking = isCurrentlyPlaying;
      wasPlayingBeforeSeekingRef.current = isCurrentlyPlaying;

      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
      isHorizontalSwipe = false;
      isVerticalSwipe = false;
      hasStartedSeeking = false;

      // Pre-calculate element width to avoid repeated getBoundingClientRect calls during dragging
      if (elementWidthRef.current === 0) {
        elementWidthRef.current = element.getBoundingClientRect().width;
      }

      // Set up long press detection - trigger 2x speed playback after 1 second
      longPressTimeoutRef.current = setTimeout(() => {
        if (
          !isHorizontalSwipe &&
          !isVerticalSwipe &&
          !hasStartedSeeking &&
          playState === PLAY_STATE.Playing
        ) {
          setIsSpeedUp(true);
          setShouldHideUIDelayed(true);

          // Clear any pending hide UI timeout
          if (hideUITimeout.current) {
            clearTimeout(hideUITimeout.current);
            hideUITimeout.current = null;
          }

          if (player && typeof player.playbackRate === 'function') {
            player.playbackRate(2.0);
          }
          onSpeedUpStart();

          // Provide haptic feedback (if supported)
          if (navigator.vibrate) {
            navigator.vibrate(50);
          }
        }
      }, LONG_PRESS_DELAY);
    };

    const handleTouchMoveNonPassive = event => {
      if (!isActive || !player) return;

      const touch = event.touches && event.touches[0];
      if (!touch) return;

      const { x: startX, y: startY } = touchStartRef.current;

      if (!startX || !startY) return;

      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      // Check for vertical swipe first - if detected, block all horizontal actions
      if (
        !isVerticalSwipe &&
        !isHorizontalSwipe &&
        absDeltaY > VERTICAL_THRESHOLD
      ) {
        isVerticalSwipe = true;

        // Cancel long press speed up detection immediately
        if (longPressTimeoutRef.current) {
          clearTimeout(longPressTimeoutRef.current);
          longPressTimeoutRef.current = null;
        }

        // If currently in speed up mode, restore normal speed
        if (isSpeedUp) {
          setIsSpeedUp(false);
          if (player && typeof player.playbackRate === 'function') {
            player.playbackRate(1.0);
          }
          onSpeedUpEnd();
        }

        // Reset shouldHideUIDelayed immediately when vertical swipe is detected
        // Clear any existing timeout first
        if (hideUITimeout.current) {
          clearTimeout(hideUITimeout.current);
          hideUITimeout.current = null;
        }
        setShouldHideUIDelayed(false);

        return; // Don't process horizontal actions
      }

      // If vertical swipe is detected, don't handle horizontal actions
      if (isVerticalSwipe) return;

      // Only handle when confirmed as horizontal swipe
      if (
        !isHorizontalSwipe &&
        absDeltaX > HORIZONTAL_THRESHOLD &&
        absDeltaX > absDeltaY * HORIZONTAL_RATIO
      ) {
        isHorizontalSwipe = true;
        event.preventDefault();

        // Cancel long press speed up detection
        if (longPressTimeoutRef.current) {
          clearTimeout(longPressTimeoutRef.current);
          longPressTimeoutRef.current = null;
        }

        // If currently in speed up mode, restore normal speed
        if (isSpeedUp) {
          setIsSpeedUp(false);
          if (player && typeof player.playbackRate === 'function') {
            player.playbackRate(1.0);
          }
          onSpeedUpEnd();
          // Don't clear shouldHideUIDelayed here since we're starting seeking
        }
      }

      // If not horizontal swipe, don't handle
      if (!isHorizontalSwipe) return;

      // Prevent event bubbling to upper layer swipe switching
      event.preventDefault();
      event.stopPropagation();

      if (!hasStartedSeeking) {
        hasStartedSeeking = true;
        setIsSeeking(true);
        setShouldShowSeekBarOverlay(true);
        setShouldHideUIDelayed(true);

        // Clear any pending hide UI timeout
        if (hideUITimeout.current) {
          clearTimeout(hideUITimeout.current);
          hideUITimeout.current = null;
        }

        onSeekStart();

        // Only pause video if it was playing before dragging
        if (wasPlayingBeforeSeeking && player) {
          pause();
        }
      }

      // Get player time information
      const playerDuration = player?.duration?.() || duration;

      if (playerDuration <= 0) return;

      // Improve sensitivity calculation using cached width
      const screenFactor = Math.max(
        300,
        Math.min(600, elementWidthRef.current)
      );
      // Fine-tune sensitivity for smooth dragging
      const seekSensitivity = (playerDuration / screenFactor) * 2;

      // Simple and direct calculation - no smoothing constraints
      const baseTime = seekingStartTimeRef.current || startTime;
      const targetTime = Math.max(
        0,
        Math.min(playerDuration - 0.1, baseTime + deltaX * seekSensitivity)
      );

      // Update UI display time immediately without any threshold restrictions
      lastSeekTimeRef.current = targetTime;
      setCurrentTime(targetTime);

      // Clear previous debounce timeout
      if (seekDebounceRef.current) {
        clearTimeout(seekDebounceRef.current);
      }

      // Debounce player updates to avoid excessive player method calls
      seekDebounceRef.current = setTimeout(() => {
        if (player && typeof player.currentTime === 'function') {
          player.currentTime(targetTime);
        }
      }, SEEK_DEBOUNCE_DELAY);
    };

    const handleTouchEndNonPassive = () => {
      // Clear long press detection
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
        longPressTimeoutRef.current = null;
      }

      // Clear any existing hide UI timeout first
      if (hideUITimeout.current) {
        clearTimeout(hideUITimeout.current);
        hideUITimeout.current = null;
      }

      // If currently in speed up mode, restore normal speed
      if (isSpeedUp) {
        setIsSpeedUp(false);
        if (player && typeof player.playbackRate === 'function') {
          player.playbackRate(1.0);
        }
        onSpeedUpEnd();

        // Add delay for speed up end to prevent button flash
        hideUITimeout.current = setTimeout(() => {
          setShouldHideUIDelayed(false);
        }, 50);
      } else if (hasStartedSeeking) {
        // Check if we actually moved significantly during seeking
        const currentPlayerTime = player?.currentTime?.() || 0;
        const timeDiff = Math.abs(
          currentPlayerTime - seekingStartTimeRef.current
        );
        const hasSignificantMovement = timeDiff > 0.5; // More than 0.5 seconds difference

        // Ensure the last seek is executed
        if (seekDebounceRef.current) {
          clearTimeout(seekDebounceRef.current);
          if (player && typeof player.currentTime === 'function') {
            player.currentTime(lastSeekTimeRef.current);
          }
        }

        // Delay hiding progress bar
        clearTimeout(seekTimeout.current);
        seekTimeout.current = setTimeout(() => {
          setShouldShowSeekBarOverlay(false);
        }, PROGRESS_BAR_HIDE_DELAY);

        setIsSeeking(false);
        onSeekEnd();

        // Restore playback state - use value saved in ref
        const shouldResumePlay = wasPlayingBeforeSeekingRef.current;
        if (player && shouldResumePlay) {
          setTimeout(() => {
            play();
          }, PLAY_RESUME_DELAY);
        }

        // If there was significant movement, delay hiding UI
        // Otherwise, reset immediately
        if (hasSignificantMovement) {
          hideUITimeout.current = setTimeout(() => {
            setShouldHideUIDelayed(false);
          }, 100);
        } else {
          // No significant movement, reset immediately
          setShouldHideUIDelayed(false);
        }
      } else {
        // If we didn't start seeking and not in speed up mode,
        // reset shouldHideUIDelayed immediately
        setShouldHideUIDelayed(false);
      }

      // Reset all variables - keep elementWidthRef cached to avoid repeated calculations
      touchStartRef.current = { x: 0, y: 0 };
      isHorizontalSwipe = false;
      isVerticalSwipe = false;
      hasStartedSeeking = false;
      startTime = 0;
      seekingStartTimeRef.current = 0;
      wasPlayingBeforeSeeking = false;
      wasPlayingBeforeSeekingRef.current = false;
    };

    // Add event listeners with passive: false
    element.addEventListener('touchstart', handleTouchStartNonPassive, {
      passive: false,
    });
    element.addEventListener('touchmove', handleTouchMoveNonPassive, {
      passive: false,
    });
    element.addEventListener('touchend', handleTouchEndNonPassive, {
      passive: false,
    });

    // Add click event listener for double click detection
    element.addEventListener('click', handleClickEvent);

    return () => {
      if (seekDebounceRef.current) {
        clearTimeout(seekDebounceRef.current);
      }
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
      if (seekTimeout.current) {
        clearTimeout(seekTimeout.current);
      }
      if (hideUITimeout.current) {
        clearTimeout(hideUITimeout.current);
      }
      if (doubleClickTimeoutRef.current) {
        clearTimeout(doubleClickTimeoutRef.current);
      }
      element.removeEventListener('touchstart', handleTouchStartNonPassive);
      element.removeEventListener('touchmove', handleTouchMoveNonPassive);
      element.removeEventListener('touchend', handleTouchEndNonPassive);
      element.removeEventListener('click', handleClickEvent);
    };
  }, [
    element,
    player,
    isActive,
    duration,
    playState,
    isSpeedUp,
    onSeekStart,
    onSeekEnd,
    onSpeedUpStart,
    onSpeedUpEnd,
    onProgressUpdate,
    onDoubleClickFastForward,
    onSingleClick,
    pause,
    play,
    handleClickEvent,
  ]);

  // Mouse long press event handlers (for desktop support)
  useEffect(() => {
    if (!element || !player) return;

    const handleMouseDown = event => {
      // Don't handle long press on seek bar or other interactive elements
      // Only handle when clicking directly on the main element
      if (event.target !== element || !isActive || !player) {
        return;
      }

      isMouseDown.current = true;
      hasMouseMoved.current = false;
      mouseDownStartTime.current = Date.now();

      // Start long press detection - trigger 2x speed after 300ms
      mouseLongPressTimeout.current = setTimeout(() => {
        if (
          isMouseDown.current &&
          !hasMouseMoved.current &&
          playState === PLAY_STATE.Playing &&
          !isSeeking // Don't trigger if already seeking
        ) {
          setIsSpeedUp(true);
          setShouldHideUIDelayed(true);

          // Clear any pending hide UI timeout
          if (hideUITimeout.current) {
            clearTimeout(hideUITimeout.current);
            hideUITimeout.current = null;
          }

          if (player && typeof player.playbackRate === 'function') {
            player.playbackRate(2.0);
          }
          onSpeedUpStart();

          // Provide haptic feedback if supported
          if (navigator.vibrate) {
            navigator.vibrate(50);
          }
        }
      }, LONG_PRESS_DELAY);
    };

    const handleMouseMove = () => {
      if (isMouseDown.current) {
        hasMouseMoved.current = true;

        // Cancel long press if mouse moved
        if (mouseLongPressTimeout.current) {
          clearTimeout(mouseLongPressTimeout.current);
          mouseLongPressTimeout.current = null;
        }

        // If already in speed up mode, exit it
        if (isSpeedUp) {
          setIsSpeedUp(false);
          if (player && typeof player.playbackRate === 'function') {
            player.playbackRate(1.0);
          }
          onSpeedUpEnd();
        }
      }
    };

    const handleMouseUp = () => {
      // Clear long press detection
      if (mouseLongPressTimeout.current) {
        clearTimeout(mouseLongPressTimeout.current);
        mouseLongPressTimeout.current = null;
      }

      // If in speed up mode, restore normal speed
      if (isSpeedUp && isMouseDown.current) {
        setIsSpeedUp(false);
        if (player && typeof player.playbackRate === 'function') {
          player.playbackRate(1.0);
        }
        onSpeedUpEnd();
        // Add delay to prevent UI flash
        if (hideUITimeout.current) {
          clearTimeout(hideUITimeout.current);
          hideUITimeout.current = null;
        }
        hideUITimeout.current = setTimeout(() => {
          setShouldHideUIDelayed(false);
        }, 50);
      }

      // Reset mouse states
      isMouseDown.current = false;
      hasMouseMoved.current = false;
      mouseDownStartTime.current = 0;
    };

    // Add mouse event listeners
    element.addEventListener('mousedown', handleMouseDown);
    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseup', handleMouseUp);

    return () => {
      element.removeEventListener('mousedown', handleMouseDown);
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseup', handleMouseUp);

      // Cleanup mouse long press timeout
      if (mouseLongPressTimeout.current) {
        clearTimeout(mouseLongPressTimeout.current);
        mouseLongPressTimeout.current = null;
      }
    };
  }, [
    element,
    player,
    isActive,
    playState,
    isSpeedUp,
    isSeeking,
    onSpeedUpStart,
    onSpeedUpEnd,
  ]);
  // Update current time when not seeking
  useEffect(() => {
    if (!isSeeking && player) {
      // update current time immediately
      const updateCurrentTime = () => {
        const playerCurrentTime = player?.currentTime?.();
        if (playerCurrentTime !== undefined) {
          setCurrentTime(playerCurrentTime);
        }
      };

      // execute immediately
      updateCurrentTime();

      // if playing, set interval to update current time
      let interval;
      if (playState === PLAY_STATE.Playing) {
        interval = setInterval(updateCurrentTime, 100); // update every 100ms when playing
      }

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }
  }, [isSeeking, playState, player]);

  // Reset UI state when component becomes inactive
  useEffect(() => {
    if (!isActive) {
      // Clear any pending timeouts
      if (hideUITimeout.current) {
        clearTimeout(hideUITimeout.current);
        hideUITimeout.current = null;
      }
      if (seekTimeout.current) {
        clearTimeout(seekTimeout.current);
        seekTimeout.current = null;
      }
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
        longPressTimeoutRef.current = null;
      }
      if (doubleClickTimeoutRef.current) {
        clearTimeout(doubleClickTimeoutRef.current);
        doubleClickTimeoutRef.current = null;
      }
      if (mouseLongPressTimeout.current) {
        clearTimeout(mouseLongPressTimeout.current);
        mouseLongPressTimeout.current = null;
      }

      // Reset all UI states
      setShouldHideUIDelayed(false);
      setShouldShowSeekBarOverlay(false);
      setIsSeeking(false);
      setIsSpeedUp(false);

      // Reset double click state
      clickCountRef.current = 0;
      lastClickTimeRef.current = 0;

      // Reset mouse states
      isMouseDown.current = false;
      hasMouseMoved.current = false;
      mouseDownStartTime.current = 0;
    }
  }, [isActive]);

  // Safety mechanism: auto-reset shouldHideUIDelayed if it's stuck
  useEffect(() => {
    let safetyTimeout;
    if (shouldHideUIDelayed && !isSeeking && !isSpeedUp) {
      // If shouldHideUIDelayed is true but we're not seeking or speeding up,
      // reset it after a short delay
      safetyTimeout = setTimeout(() => {
        setShouldHideUIDelayed(false);
      }, 300);
    }
    return () => {
      if (safetyTimeout) {
        clearTimeout(safetyTimeout);
      }
    };
  }, [shouldHideUIDelayed, isSeeking, isSpeedUp]);

  // Keyboard event listener - left/right arrow keys control fast forward/rewind
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = event => {
      // Avoid triggering in input fields
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.contentEditable === 'true'
      ) {
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          handleKeyboardSeek(true); // Rewind
          break;
        case 'ArrowRight':
          event.preventDefault();
          handleKeyboardSeek(false); // Fast forward
          break;
        default:
          break;
      }
    };

    // Add keyboard event listener
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, handleKeyboardSeek]);

  return {
    // States
    isSeeking,
    isSpeedUp,
    currentTime,
    shouldShowSeekBarOverlay,

    // Utilities
    formatTime,
    displayCurrentTime: currentDisplayTime, // Use pre-calculated value
    displayDuration: currentDisplayDuration, // Use pre-calculated value

    // Control flags
    shouldHideUI: isSeeking || isSpeedUp || shouldHideUIDelayed,

    // Debug info
    shouldHideUIDelayed,
  };
};

export default useHorizontalSeek;
